import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { User } from './types';
import { AuthPage } from './components/AuthPage';
import { SwipePage } from './pages/SwipePage';
import { ChatPage } from './pages/ChatPage';
import { ConversationsPage } from './pages/ConversationsPage';
import { ProfilePage } from './pages/ProfilePage';
import { CreateProjectPage } from './pages/CreateProjectPage';
import { OfflineIndicator } from './components/OfflineIndicator';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { AuthService } from './services/authService';

function App() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Verificar sesión existente al iniciar la app
    const checkExistingSession = async () => {
      try {
        console.log('🔍 Verificando sesión existente...');
        
        const sessionResult = await AuthService.verifySession();
        
        if (sessionResult.success && sessionResult.user) {
          console.log('✅ Sesión válida encontrada:', sessionResult.user.name);
          setCurrentUser(sessionResult.user);
          setIsAuthenticated(true);
        } else {
          console.log('❌ No hay sesión válida:', sessionResult.error);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('❌ Error al verificar sesión:', error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkExistingSession();
  }, []);

  const handleAuthSuccess = (userData: User) => {
    console.log('🎉 Autenticación exitosa en App:', userData.name);
    setCurrentUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await AuthService.logout();
      setCurrentUser(null);
      setIsAuthenticated(false);
      console.log('👋 Sesión cerrada');
    } catch (error) {
      console.error('❌ Error al cerrar sesión:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // Si no está autenticado, mostrar página de autenticación
  if (!isAuthenticated || !currentUser) {
    return (
      <div className="min-h-screen bg-gray-50">
        <OfflineIndicator />
        <PWAInstallPrompt />
        <AuthPage onAuthSuccess={handleAuthSuccess} />
      </div>
    );
  }

  // Main application with routing
  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <OfflineIndicator />
        <PWAInstallPrompt />
        
        <Routes>
          <Route path="/" element={<SwipePage currentUser={currentUser} />} />
          <Route 
            path="/swipe" 
            element={<SwipePage currentUser={currentUser} />} 
          />
          <Route 
            path="/chat/:conversationId" 
            element={<ChatPage currentUser={currentUser} />} 
          />
          <Route 
            path="/conversations" 
            element={<ConversationsPage currentUser={currentUser} />} 
          />
          <Route 
            path="/profile" 
            element={
              <ProfilePage 
                currentUser={currentUser} 
                onUserUpdate={setCurrentUser}
                onLogout={handleLogout}
              />
            } 
          />
          <Route 
            path="/create-project" 
            element={<CreateProjectPage currentUser={currentUser} />} 
          />
          <Route path="*" element={<Navigate to="/swipe" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;